import { Link } from "@tanstack/react-router";

import { PolicyDocument, PolicySection } from "../components/PolicyDocument.js";
import { legalParties, PRIVACY_LAST_UPDATED } from "../lib/legal.js";
import { useTenant } from "../lib/tenant.js";

const SECTIONS = [
  { id: "scope", title: "Scope" },
  { id: "personal-information-we-collect", title: "Personal Information We Collect" },
  { id: "how-we-use-personal-information", title: "How We Use Personal Information" },
  {
    id: "how-we-may-disclose-personal-information",
    title: "How We May Disclose Personal Information",
  },
  { id: "cookies-and-other-tracking-mechanisms", title: "Cookies and Other Tracking Mechanisms" },
  { id: "third-party-links", title: "Third-Party Links" },
  { id: "user-generated-content", title: "User Generated Content" },
  { id: "your-privacy-choices", title: "Your Privacy Choices" },
  { id: "children", title: "Children" },
  { id: "security", title: "Security" },
  { id: "changes-to-this-policy", title: "Changes to This Policy" },
  { id: "contact-us", title: "Contact Us" },
] as const;

/**
 * The Privacy Policy, ported from boxed.gg's and read against this storefront's legal identity
 * and brand. The providers named are the ones every storefront on the platform runs on
 * (payments, identity verification, fraud screening, address validation, live chat, cookie
 * consent, error monitoring, and the platform itself) plus the platform's own first-party
 * analytics; boxed's analytics and advertising vendors are not loaded here and are not named.
 */
export function PrivacyPage() {
  const { config } = useTenant();
  const p = legalParties(config);

  return (
    <PolicyDocument title="Privacy Policy" lastUpdated={PRIVACY_LAST_UPDATED}>
      <PolicySection id="scope" title="Scope">
        <p>
          This Privacy Policy (“Policy”) describes how {p.company} and its affiliates,
          (collectively, “we,” “us” and “our”) collect, use, and disclose your personal information
          when you visit our websites (“Sites”) or use any of our products or services
          (collectively, “Services”) that link to this Policy. This Policy does not apply to the
          personal information that we collect and process about personnel or applicants. Your use
          of the Services is subject to this Policy and our <Link to="/terms">Terms</Link>.
        </p>
        <p>
          The Services run on Loot OS, a platform operated by Loot Labs Inc., which hosts the Sites,
          processes personal information for the purposes described in this Policy, and fulfills the
          physical items you withdraw.
        </p>
        <ul>
          {SECTIONS.map((section) => (
            <li key={section.id}>
              <a href={`#${section.id}`}>{section.title}</a>
            </li>
          ))}
        </ul>
      </PolicySection>

      <PolicySection id="personal-information-we-collect" title="Personal Information We Collect">
        <p>
          <strong>Personal Information We Collect From You.</strong> The personal information we
          collect from you depends upon how you use our Services or otherwise interact or engage
          with us, but generally includes:
        </p>
        <ul>
          <li>
            Identifiers and Contact Information. We may collect personal identifiers and contact
            information such as your name, address, email address, and phone number.
          </li>
          <li>
            Profile and Account Details. If you register an account with us, we collect the email
            address you sign in with and the username you choose, which is shown to other users.
          </li>
          <li>
            Commercial Information. We collect information about your purchases, the boxes you open,
            the battles you play, the items you hold, sell back or withdraw, and your use of our
            Site and Services.
          </li>
          <li>
            Shipping Details. If you withdraw a physical item, we collect the shipping address you
            ask us to deliver it to.
          </li>
          <li>
            Identity Verification. Where we ask you to verify your identity, our identity
            verification provider collects your identity document and a photograph of you and
            reports the result to us. We store the outcome, the date, and an irreversible hash used
            to detect duplicate accounts; we do not store your name, date of birth or the document
            itself.
          </li>
          <li>
            User and Message Contents. When you contact our customer service team, including through
            our third-party chat feature or by email, we may collect your messages, emails, or any
            other personal information you choose to provide.
          </li>
          <li>
            Preferences. We may also collect personal information about the types of Services you
            use, your communications preferences, and other preferences you may select in your
            account or profile.
          </li>
          <li>
            Other Information. We also collect the personal information you provide when you
            complete online forms or surveys. We may also collect any other personal information you
            choose to provide.
          </li>
        </ul>
        <p>
          <strong>Personal Information We Collect Automatically.</strong> As described below in the
          “Cookies and Other Tracking Mechanisms” section, when you visit our Site or use our
          Services, we may automatically collect certain personal information, including:
        </p>
        <ul>
          <li>
            Identifiers and Device Information. We automatically collect IP address, unique
            identifiers, device name, type and model; browser type; operating system type, name, and
            version; information about your browser and browser language and other information.
          </li>
          <li>
            Network Activity. We may also collect information related to how you interact with our
            Site and Services, such as page views, time and length of visit; links and items
            clicked; and other activity information, referring URL, and other similar information.
            We may use fraud-prevention, error-monitoring and other providers and technologies,
            including cookies and similar tools, to collect this information.
          </li>
        </ul>
        <p>
          <strong>Personal Information We Collect from Other Sources.</strong> We may collect
          personal information about you through our affiliates, service providers (including our
          payment processor, our fraud-prevention provider and our identity verification provider),
          public and other sources. Personal information does not include publicly available
          information, such as information you post online or in our Services (such as your
          username, profile or activities) lawfully made available from government records,
          information we have a reasonable basis to believe is lawfully made available to the
          general public by you or widely distributed media, or by a person to whom you have
          disclosed the information and not restricted it to a specific audience, or deidentified or
          aggregated information. To the extent permitted by applicable law, we may combine the
          personal information we collect with publicly available or third-party sources.
        </p>
      </PolicySection>

      <PolicySection id="how-we-use-personal-information" title="How We Use Personal Information">
        <p>Generally, we may use the personal information we collect for the following purposes:</p>
        <ul>
          <li>
            Operate Our Business. We use the personal information we collect to run our day-to-day
            operations.
          </li>
          <li>
            Provide You Products and Services. We use the personal information we collect to provide
            you with our Site and Services, which may include events, promotions and other
            activities, which may have different and/or additional terms and requirements. If you
            withdraw a physical item, we use your shipping address to deliver it, and share it with
            the parties who fulfill and carry it.
          </li>
          <li>
            Communicate With You. We use the personal information we collect to communicate with you
            about your use of our Sites and Services, to send you the links you sign in with, to
            respond to your inquiries, to provide you with the information you request, and for
            other customer service purposes.
          </li>
          <li>
            Evaluate and Improve Our Site and Services. We use the personal information we collect
            to evaluate, analyze, improve, and develop our products and Services, including this
            Site. We also use this information to tailor the content and information that we may
            send or display to you, and to otherwise personalize your experiences while using our
            Site and Services.
          </li>
          <li>
            Marketing and Advertising. We may use your personal information for marketing and
            promotional purposes, including to send you promotional communications about products
            and services we believe may interest you. We may send communications via e-mail or
            regular mail.
          </li>
          <li>
            Inferences. We may use the information we collect to better understand how you access
            and use our Site and Services, both on an aggregated and individualized basis.
          </li>
          <li>
            Fraud, Identity and Security Purposes. We use the personal information we collect to
            mitigate fraud and protect ourselves and others, including by screening sign-ups,
            purchases and withdrawals with our fraud-prevention provider and by verifying your
            identity and age with our identity verification provider. We also use this information
            to prevent and detect security issues, malicious or deceptive behavior or activities,
            infringements of our policies and contracts and other potential misuse of or illegal
            activity.
          </li>
          <li>
            Legal Requirements. We use the personal information we collect to investigate, enforce,
            and apply this Policy and our Terms, and to otherwise protect our own rights and
            interests, such as to resolve any disputes or to respond to requests from law
            enforcement, court orders, regulators, or other legal processes.
          </li>
          <li>
            Other Operational and Business Purposes. We may use your personal information for other
            operational purposes such as to comply with contracts related to products or Services
            that we provide to you.
          </li>
          <li>
            Other Uses. We may use your personal information for other purposes that we notify you
            of or obtain your consent.
          </li>
        </ul>
      </PolicySection>

      <PolicySection
        id="how-we-may-disclose-personal-information"
        title="How We May Disclose Personal Information"
      >
        <p>
          When permitted by applicable law, we may disclose your personal information to our
          affiliates, service providers, and others.
        </p>
        <ul>
          <li>
            Service Providers. We may disclose the personal information we collect with service
            providers who perform functions on our behalf, such as the platform that hosts the
            Services and fulfills withdrawals (Loot Labs Inc.), payment processing (Stripe),
            identity verification (Veriff), fraud prevention (SEON), shipping address validation and
            the carriers that deliver your items, live chat and customer support (Intercom), cookie
            consent management (Termly), error monitoring, email delivery, development and research.
          </li>
          <li>
            Marketing and Advertising Partners. We may also disclose your personal information, such
            as name and contact information, with our marketing and advertising partners to send you
            content about certain products and Services.
          </li>
        </ul>
        <p>We also may disclose your personal information in the following circumstances:</p>
        <ul>
          <li>
            Business Transfers. We may disclose personal information as part of commercial
            transactions (e.g., mergers, acquisitions, bankruptcy, or other similar business
            transactions) and in contemplation of such transactions (e.g., due diligence).
          </li>
          <li>
            Legal Purposes. Personal information may be disclosed to third parties, as permitted by
            law or subpoena, or if we reasonably believe such action is necessary to comply with the
            law and the reasonable requests of regulators, law enforcement, other public
            authorities, or other similar purposes.
          </li>
          <li>
            Security and Protection of Rights. We also may share and/or use your information to
            protect the safety, rights, property, or security of Company, our Site and Services, any
            third party, or the general public; to detect, prevent, or otherwise address fraud,
            security, cybersecurity, cybercrimes or technical issues; to prevent or stop any
            activity that we, in our sole discretion, may consider to be, or to pose a risk of
            being, illegal, unethical, or legally actionable; to use as evidence in litigation; to
            conduct audits; and/or to enforce our Terms.
          </li>
          <li>
            Aggregate and De-Identified Information. We may disclose aggregate or de-identified
            personal information about users with third parties for marketing, advertising, research
            and any other purposes.
          </li>
          <li>
            Other Disclosures. We may disclose your personal information for other purposes that we
            notify you of or obtain your consent.
          </li>
        </ul>
      </PolicySection>

      <PolicySection
        id="cookies-and-other-tracking-mechanisms"
        title="Cookies and Other Tracking Mechanisms"
      >
        <p>
          We and our third-party service providers use cookies, pixels, tags, and other similar
          tracking mechanisms to automatically collect personal information about browsing activity,
          type of device and similar information within our Services. We use this information to,
          for example, keep you signed in, analyze and understand how users access, use, and
          interact with the Services, as well to identify and resolve bugs and errors and to assess,
          secure, protect, optimize and improve the performance of our Services.
        </p>
        <p>
          <strong>Cookies, Clear GIFs, Pixel Tags, and Other Technologies.</strong> “Cookies” are
          alphanumeric identifiers we transfer to your device through your browser for tracking,
          measurement, security and other purposes. Some cookies allow us to make it easier for you
          to navigate our Services, support the security and performance of the Services, or allow
          us to track activity and usage data within our Service. In addition to cookies, we may
          also use pixel tags (sometimes called web beacons or “clear GIFs”) to collect personal
          information about you and your use of our Services. While cookies are stored locally on
          your device, pixel tags are embedded invisibly within web pages and online content. We may
          use these, in connection with our Services to, among other things, track the activities of
          users, help us manage content and compile usage statistics. We may also use these in HTML
          e-mails we send, to help us track e-mail response rates, identify when our e-mails are
          viewed, and track whether our e-mails are forwarded.
        </p>
        <p>
          <strong>Fraud Prevention and Error Monitoring.</strong> Our fraud-prevention provider,
          SEON, collects information about the device and browser you use, such as its
          configuration, network and a device identifier, when you sign up, purchase or withdraw, in
          order to assess the risk of the transaction. Our error-monitoring provider records the
          technical details of errors you encounter, including your browser and the pages involved.
          These services record information such as purchase events, site navigation and profile
          changes.
        </p>
        {/* Placeholder wording for legal: the first-party analytics disclosure (docs/analytics.md). */}
        <p>
          <strong>First-Party Analytics.</strong> Where the operator of this Site has enabled
          analytics, and, where a cookie banner is shown, only after you have consented to
          analytics, the platform records how the Site is used: the pages you view, the boxes you
          open, battles you take part in, purchases you start, and errors you encounter. These
          records carry a random identifier stored in your browser, a per-visit identifier, your
          browser's user-agent string and, when you are signed in, the identifier of your account;
          they never carry your name, email address, postal address or IP address. They are kept for
          up to 13 months and are used to operate, measure and improve the Site. Withdrawing your
          analytics consent, where a banner is shown, removes the identifier from your browser.
        </p>
        <p>
          <strong>Cookie Consent.</strong> Where we show a cookie banner, our consent management
          provider, Termly, records the choices you make in your browser's local storage for this
          Site so that they are honored on later visits, keeps a record of that consent, and may use
          your approximate location to determine which notice to show you. You can change your
          choices at any time from the “Cookie preferences” link in the footer of every page.
        </p>
        <p>
          We use Stripe for payment, analytics, and other business services. Stripe collects
          identifying information about the devices that connect to its services. Stripe uses this
          information to operate and improve the services it provides to us, including for fraud
          detection. You can learn more about Stripe and read its privacy policy at{" "}
          <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
            https://stripe.com/privacy
          </a>
          .
        </p>
        <p>
          <strong>Cross-Device Tracking.</strong> We and our third-party providers may use the
          personal information that we collect about you within our Services and on other
          third-party sites and services to help us and these third parties to identify other
          devices that you use (e.g., a mobile phone, tablet, other computer, etc.).
        </p>
        <p>
          <strong>Do-Not-Track.</strong> Currently, our systems do not recognize browser
          “do-not-track” requests. You may, however, disable certain tracking as discussed in this
          section (e.g., by disabling cookies).
        </p>
      </PolicySection>

      <PolicySection id="third-party-links" title="Third-Party Links">
        <p>
          Our Site and Services may contain links to third-party websites. Any access to and use of
          such linked websites is not governed by this Policy, but instead is governed by the
          privacy policies of those third party websites. We are not responsible for the information
          practices of such third party websites.
        </p>
      </PolicySection>

      <PolicySection id="user-generated-content" title="User Generated Content">
        <p>
          The username you choose, and your activity on the Services, such as the boxes you open and
          the battles you play, are shown to other users of the Site. If you post content to our
          Site or Services, all of the information that you post will be available to all visitors
          to our Site and Services. If you post your own content on our Site or Services, your
          posting may become public and may be forwarded to others. We cannot prevent such
          information from being used in a manner that may violate this Policy, the law, or your
          personal privacy. We reserve the right to remove any content, but we do not actively
          monitor our Site and Services at all times. Please contact us as described below with any
          concerns.
        </p>
      </PolicySection>

      <PolicySection id="your-privacy-choices" title="Your Privacy Choices">
        <p>We provide several ways for you to manage choices about your personal information.</p>
        <ul>
          <li>
            Cookie Settings. If you wish to prevent cookies and other technologies from tracking
            your activity on our Sites and Services or visits across multiple websites, you can set
            your browser to block certain cookies or notify you when a cookie is set; you can also
            delete cookies. The “Help” portion of the toolbar on most browsers will tell you how to
            prevent your device from accepting new cookies, how to have the browser notify you when
            you receive a new cookie, or how to delete cookies. Visitors to our Sites who disable
            cookies will be able to browse the Sites, but some features, including signing in, may
            not function. Where a cookie banner is shown, you can also change the choices you made
            in it from the “Cookie preferences” link in the footer of every page.
          </li>
          <li>
            Marketing Communications. You can opt out of receiving marketing emails from us by using
            the unsubscribe feature in the emails. We may continue to send you transactional emails,
            including the links you sign in with.
          </li>
          <li>
            Account Information. You may access, update, or correct your account and profile
            information at any time by logging in to your account and updating the information we
            maintain or by emailing us as described below. To request deletion of your account,
            email us from the address associated with it; some information may be retained where
            required for legal, tax, fraud-prevention or compliance purposes, for the period
            required by applicable law.
          </li>
        </ul>
      </PolicySection>

      <PolicySection id="children" title="Children">
        <p>
          Our Services are not designed for children, and we do not knowingly collect personal
          information from children under the age of thirteen. If we learn that we have received
          personal information directly from a child, we will delete such information from our
          systems. If you are a parent or legal guardian and you believe we have collected your
          child’s personal information in violation of applicable law, please contact us using the
          contact information below.
        </p>
      </PolicySection>

      <PolicySection id="security" title="Security">
        <p>
          We have implemented safeguards designed to protect personal information. Please be aware
          that despite our efforts, no data security measures can guarantee security.
        </p>
      </PolicySection>

      <PolicySection id="changes-to-this-policy" title="Changes to This Policy">
        <p>
          This Privacy Policy is in effect as of the date noted at the top of the Privacy Policy. We
          may change this Privacy Policy from time to time. If we do, we will post the revised
          version here and change the “last updated date” (the date it applies from) at the top of
          the Privacy Policy. You should check here regularly for the most up-to-date version of the
          Privacy Policy.
        </p>
      </PolicySection>

      <PolicySection id="contact-us" title="Contact Us">
        <p>
          If you have any questions about this Policy or otherwise need to contact Company for any
          reason, you can reach us at <a href={`mailto:${p.supportEmail}`}>{p.supportEmail}</a>.
        </p>
      </PolicySection>
    </PolicyDocument>
  );
}
